import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../auth/useAuth';
import authInstance from '../../firebase/auth';
import app from '../../firebase/config';
import './admin.css';

const functions = getFunctions(app, 'europe-west1');

const TRUST_KEY = (email) => `ts_trust_${email}`;

function loadTrustData(email) {
  try {
    const raw = localStorage.getItem(TRUST_KEY(email));
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (new Date(data.expiresAt) < new Date()) {
      localStorage.removeItem(TRUST_KEY(email));
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function saveTrustData(email, uid, token, expiresAt) {
  localStorage.setItem(TRUST_KEY(email), JSON.stringify({ uid, token, expiresAt }));
}

export function clearTrustData(email) {
  if (email) localStorage.removeItem(TRUST_KEY(email));
}

const FIREBASE_ERROR_MESSAGES = {
  'auth/invalid-credential':          'E-Mail oder Passwort falsch.',
  'auth/wrong-password':              'Passwort falsch.',
  'auth/user-not-found':              'Kein Konto mit dieser E-Mail-Adresse gefunden.',
  'auth/user-disabled':               'Dieses Konto wurde deaktiviert.',
  'auth/too-many-requests':           'Zu viele Fehlversuche. Bitte warte einen Moment oder setze dein Passwort zurück.',
  'auth/multi-factor-auth-required':  '2FA-Code erforderlich.',
  'auth/network-request-failed':      'Keine Verbindung. Bitte Internetverbindung prüfen.',
  'auth/invalid-email':               'Ungültige E-Mail-Adresse.',
  'auth/internal-error':              'Interner Fehler. Bitte Seite neu laden und erneut versuchen.',
};

function toGermanError(code) {
  return FIREBASE_ERROR_MESSAGES[code] || `Anmeldung fehlgeschlagen (${code || 'unbekannt'}). Bitte erneut versuchen.`;
}

export default function AdminLogin({ variant = 'admin' }) {
  const { login, resolveTotpMfa } = useAuth();
  const navigate   = useNavigate();
  const location   = useLocation();
  const redirectTo = location.state?.from?.pathname || (variant === 'user' ? '/work' : '/admin');
  const brandingLabel = variant === 'user'
    ? (redirectTo.startsWith('/work') ? 'techsoul – work' : 'techsoul – life')
    : 'techsoul admin';

  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [totpCode, setTotpCode]     = useState('');
  const [trustBrowser, setTrustBrowser] = useState(false);
  const [step, setStep]             = useState('credentials'); // 'credentials' | 'mfa'
  const [mfaError, setMfaError]     = useState(null);
  const [error, setError]           = useState('');
  const [loading, setLoading]       = useState(false);

  async function handleCredentials(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err.code === 'auth/multi-factor-auth-required') {
        // Browser vertraut? → TOTP überspringen
        const trust = loadTrustData(email);
        if (trust) {
          try {
            const signInFn = httpsCallable(functions, 'signInWithTrustToken');
            const { data } = await signInFn({ uid: trust.uid, token: trust.token });
            await signInWithCustomToken(authInstance, data.customToken);
            navigate(redirectTo, { replace: true });
            return;
          } catch {
            // Token ungültig oder abgelaufen → normaler TOTP-Flow
            clearTrustData(email);
          }
        }
        setMfaError(err);
        setStep('mfa');
      } else {
        setError(toGermanError(err.code));
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleMfa(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userCredential = await resolveTotpMfa(mfaError, totpCode.trim());

      if (trustBrowser) {
        try {
          const createFn = httpsCallable(functions, 'createTrustToken');
          const { data } = await createFn();
          // uid aus dem aufgelösten MFA-Credential lesen
          const uid = userCredential?.user?.uid || mfaError?.resolver?.session?._mfaUser?.uid;
          if (uid) saveTrustData(email, uid, data.token, data.expiresAt);
        } catch {
          // Trust-Token-Erstellung fehlgeschlagen → Login trotzdem erfolgreich
        }
      }

      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError('Ungültiger 2FA-Code. Bitte erneut versuchen.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="admin-login-wrap">
      <div className="admin-container--narrow" style={{ width: '100%' }}>
        <div className="admin-login-logo">{brandingLabel}</div>

        <div className="admin-card">
          {step === 'credentials' ? (
            <>
              <h1 className="admin-card__title">Anmelden</h1>

              {error && <div className="admin-alert admin-alert--error">{error}</div>}

              <form className="admin-form" onSubmit={handleCredentials}>
                <div className="admin-form__group">
                  <label className="admin-form__label" htmlFor="email">E-Mail</label>
                  <input
                    id="email"
                    type="email"
                    className="admin-form__input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>

                <div className="admin-form__group">
                  <label className="admin-form__label" htmlFor="password">Passwort</label>
                  <input
                    id="password"
                    type="password"
                    className="admin-form__input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="admin-btn admin-btn--primary"
                  disabled={loading}
                >
                  {loading ? 'Anmelden…' : 'Anmelden'}
                </button>

                {variant === 'user' && (
                  <p style={{ marginTop: '1rem', fontSize: '0.85rem', textAlign: 'center', opacity: 0.75 }}>
                    Neu hier?{' '}
                    <Link to="/registrieren" style={{ color: 'var(--cyan)' }}>Jetzt registrieren</Link>
                  </p>
                )}
              </form>
            </>
          ) : (
            <>
              <h1 className="admin-card__title">2FA-Bestätigung</h1>
              <p className="admin-card__subtitle">
                Gib den 6-stelligen Code aus deiner Authenticator-App ein.
              </p>

              {error && <div className="admin-alert admin-alert--error">{error}</div>}

              <form className="admin-form" onSubmit={handleMfa}>
                <div className="admin-form__group">
                  <label className="admin-form__label" htmlFor="totp">Authenticator-Code</label>
                  <input
                    id="totp"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    className="admin-form__input"
                    placeholder="000000"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    autoComplete="one-time-code"
                    autoFocus
                    required
                  />
                </div>

                <label className="admin-trust-label">
                  <input
                    type="checkbox"
                    checked={trustBrowser}
                    onChange={(e) => setTrustBrowser(e.target.checked)}
                  />
                  Diesem Browser 30 Tage vertrauen
                </label>

                <button
                  type="submit"
                  className="admin-btn admin-btn--primary"
                  disabled={loading}
                >
                  {loading ? 'Überprüfen…' : 'Bestätigen'}
                </button>

                <button
                  type="button"
                  className="admin-btn admin-btn--secondary"
                  onClick={() => { setStep('credentials'); setError(''); setTotpCode(''); }}
                >
                  Zurück
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
